import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Eye,
  Play,
  CheckCircle2,
  TrendingUp,
  Trophy,
  BarChart3,
} from 'lucide-react';
import { useGetQuizStatsQuery } from '../../api/apiSlice';

const Stat = ({ icon: Icon, label, value, color = 'indigo' }) => {
  const palette = {
    indigo: 'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-500/10',
    emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/10',
    purple: 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-500/10',
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/10',
    amber: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/10',
  }[color];
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
          {label}
        </span>
        <div className={`p-2 rounded-lg ${palette}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
        {value}
      </div>
    </div>
  );
};

const QuizStatsPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data, isLoading, error } = useGetQuizStatsQuery(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
        {error?.data?.message || t('quiz.stats.loadError')}
      </div>
    );
  }

  const { quiz, summary, leaderboard, perQuestion } = data || {};

  return (
    <div className="max-w-5xl mx-auto">
      <Link
        to="/dashboard/student/quizzes"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> {t('quiz.stats.back')}
      </Link>

      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 break-words">
        {quiz?.title}
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        {t('quiz.stats.questions', { count: quiz?.totalQuestions || 0 })}
      </p>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat icon={Eye} label={t('quiz.stats.views')} value={summary?.views ?? 0} color="blue" />
        <Stat icon={Play} label={t('quiz.stats.starts')} value={summary?.starts ?? 0} color="indigo" />
        <Stat icon={CheckCircle2} label={t('quiz.stats.completions')} value={summary?.completions ?? 0} color="emerald" />
        <Stat icon={TrendingUp} label={t('quiz.stats.avgPercent')} value={`${summary?.avgPercentage ?? 0}%`} color="purple" />
        <Stat icon={Trophy} label={t('quiz.stats.topPercent')} value={`${summary?.topPercentage ?? 0}%`} color="amber" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Leaderboard */}
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('quiz.stats.leaderboard')}</h2>
            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
              {t('quiz.stats.completedRate', { count: summary?.completions ?? 0, rate: summary?.completionRate ?? 0 })}
            </span>
          </div>
          {leaderboard?.length > 0 ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {leaderboard.map((row, i) => (
                <div
                  key={String(row._id) + i}
                  className="flex items-center gap-3 px-5 py-3"
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      i === 0
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                        : i === 1
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        : i === 2
                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate break-words">
                      {row.participantName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(row.completedAt).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                      {row.percentage}%
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                      {row.score}/{row.totalQuestions}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('quiz.stats.emptyLeaderboard')}
            </div>
          )}
        </div>

        {/* Per-question correctness */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-500" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('quiz.stats.questionDifficulty')}
            </h2>
          </div>
          <div className="p-5 space-y-3">
            {perQuestion?.length ? (
              perQuestion.map((q, i) => {
                const pct = q.totalCount ? Math.round((q.correctCount / q.totalCount) * 100) : 0;
                return (
                  <div key={String(q.questionId)}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2 break-words">
                        {i + 1}. {q.text}
                      </p>
                      <span className="shrink-0 text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
                        {pct}%
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                      <div
                        className={`h-full transition-all ${
                          pct >= 70
                            ? 'bg-emerald-500'
                            : pct >= 40
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('quiz.stats.noData')}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizStatsPage;
